import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNewApiCall } from 'hooks';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { markDeletedItems } from 'utils/markDeletedItems';

import { PlanningAlgorithmService } from 'components/services/PlanningAlgorithmService';
import { TaskService } from 'components/services/TaskService';

import { TaskTypeSchema } from '../constants/schemas/TaskTypeSchema';
import { DEFAULT_TASK_TYPE_REQUIRED_FIELDS } from '../constants/TasksTypeTemplate';
import useTaskTypesService from '../hooks/services/useTaskTypesService';
import { TaskType } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { TasksTypeProps } from '../types/props';

import '../ui/css/EmployeeCard.css';
import { CustomDropdown } from './custom-controls';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TagsComponent } from './TagsComponent';
import TaskTypeRequiredFieldsControl from './TaskTypeRequiredFieldsControl';

interface RefInterface {
  handleCancel: () => void;
}

interface PlannedOperationOption extends ObjectForSelection {
  effort?: number | null;
  key?: string | number;
}

const priorityOptions = [
  { id: 0, label: 'Низкий' },
  { id: 1, label: 'Средний' },
  { id: 2, label: 'Высокий' }
];

const toDisplayTime = (value?: string | null): string => {
  if (!value) return '';
  const [hours = '', minutes = ''] = value.split(':');
  if ((hours.length === 2 || hours.length === 3) && minutes.length === 2) {
    return `${hours}:${minutes}`;
  }
  return value;
};

const toTimeWithSeconds = (value?: string | null): string => {
  if (!value) return '';
  const [hours = '', minutes = '', seconds = ''] = value.split(':');
  if ((hours.length === 2 || hours.length === 3) && minutes.length === 2) {
    return `${hours}:${minutes}:${seconds.length === 2 ? seconds : '00'}`;
  }
  return value;
};

const toFormTaskTypeValues = (value: TaskType): TaskType => ({
  ...value,
  requiredFields: {
    ...DEFAULT_TASK_TYPE_REQUIRED_FIELDS,
    ...value.requiredFields,
    attachment: false
  },
  assignDepartmentOnSave: value.assignDepartmentOnSave ?? false,
  responseTime: toDisplayTime(value.responseTime),
  resolutionTime: toDisplayTime(value.resolutionTime)
});

const TaskTypeCard = React.forwardRef<RefInterface, TasksTypeProps>(
  (
    { tasksTypeProps, onAdd, onUpdate, onDelete, closeDialog }: TasksTypeProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      control,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(TaskTypeSchema),
      defaultValues: {
        ...tasksTypeProps,
        statusTransition:
          tasksTypeProps.statusTransition?.id &&
          tasksTypeProps.statusTransition.id !== 0
            ? tasksTypeProps.statusTransition
            : undefined,
        planningAlgorithm:
          tasksTypeProps.planningAlgorithm?.id &&
          tasksTypeProps.planningAlgorithm.id !== 0
            ? tasksTypeProps.planningAlgorithm
            : undefined,
        priority: tasksTypeProps.priority ?? undefined,
        isCustomEffort: tasksTypeProps.isCustomEffort ?? true,
        responseTime: toDisplayTime(tasksTypeProps.responseTime),
        resolutionTime: toDisplayTime(tasksTypeProps.resolutionTime),
        requiredEmployees: tasksTypeProps.requiredEmployees ?? 1,
        requiredFields: {
          ...DEFAULT_TASK_TYPE_REQUIRED_FIELDS,
          ...tasksTypeProps.requiredFields,
          attachment: false
        },
        assignDepartmentOnSave: tasksTypeProps.assignDepartmentOnSave ?? false
      }
    });
    const {
      field: { onChange, value }
    } = useController({
      name: 'processingTime',
      control
    });

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showRequiredFieldsDialog, setShowRequiredFieldsDialog] =
      useState(false);
    const toast = useRef<Toast>(null);
    const requiredFieldsControlRef = useRef<null | {
      handleCancel: () => void;
    }>(null);
    const taskType = watch('taskType');
    const statusTransition = watch('statusTransition');
    const planningAlgorithm = watch('planningAlgorithm');
    const priority = watch('priority');
    const [taskProcessingProcesses, setTaskProcessingProcesses] = useState<
      ObjectForSelection[]
    >([]);
    const [defaultOperationsOptions, setDefaultOperationsOptions] = useState<
      ObjectForSelection[]
    >([]);
    const [planningAlgorithmOptions, setPlanningAlgorithmOptions] = useState<
      ObjectForSelection[]
    >([]);
    const businessProcessId = watch('id');
    const defaultOperations = watch('defaultOperations');
    const processingTime = watch('processingTime');
    const isCustomEffort = watch('isCustomEffort');
    const responseTime = watch('responseTime');
    const resolutionTime = watch('resolutionTime');
    const requiredEmployees = watch('requiredEmployees');
    const requiredFields = watch('requiredFields');
    const inventory = watch('inventory');
    const assignDepartmentOnSave = watch('assignDepartmentOnSave');
    const [showAutoEffortDialog, setShowAutoEffortDialog] = useState(false);
    const [recalculatedEffort, setRecalculatedEffort] = useState<number | null>(
      null
    );
    const [pendingSubmitData, setPendingSubmitData] = useState<TaskType | null>(
      null
    );
    const [isEffortManuallyTouched, setIsEffortManuallyTouched] =
      useState<boolean>(false);

    const getOperationMatchKey = useCallback(
      (operation: ObjectForSelection | PlannedOperationOption) =>
        String(
          (operation as PlannedOperationOption).id ??
            (operation as PlannedOperationOption).key ??
            ''
        ),
      []
    );

    const calculateAutoProcessingTime = useCallback(
      (operations: ObjectForSelection[] = []) => {
        const effortByOperationId = new Map<string, number>();
        defaultOperationsOptions.forEach((option) => {
          const optionEffort = (option as PlannedOperationOption).effort;
          if (
            optionEffort === null ||
            optionEffort === undefined ||
            Number.isNaN(Number(optionEffort))
          ) {
            return;
          }
          effortByOperationId.set(
            getOperationMatchKey(option),
            Number(optionEffort)
          );
        });
        return operations.reduce((sum, operation) => {
          const operationKey = getOperationMatchKey(operation);
          const effortFromOptions = effortByOperationId.get(operationKey);
          const effortFromOperation = (operation as PlannedOperationOption)
            .effort;
          const effort =
            effortFromOptions ??
            (effortFromOperation === null || effortFromOperation === undefined
              ? undefined
              : Number(effortFromOperation));
          if (effort === null || effort === undefined || Number.isNaN(effort)) {
            return sum;
          }
          return sum + Number(effort);
        }, 0);
      },
      [defaultOperationsOptions, getOperationMatchKey]
    );

    const getDefaultOperationsOptions = useCallback(async () => {
      const result = await TaskService.getOperationListForSelection();
      if (result?.data) {
        setDefaultOperationsOptions(
          result.data.map(
            (x: any) =>
              ({
                ...x,
                id:
                  x.id !== undefined && x.id !== null
                    ? Number(x.id)
                    : Number(x.key),
                effort:
                  x.effort === undefined || x.effort === null
                    ? undefined
                    : Number(x.effort)
              }) as PlannedOperationOption
          )
        );
      }
    }, []);

    const loadPlanningAlgorithmOptions = useCallback(async () => {
      PlanningAlgorithmService.getForSelection()
        .then((resp) => {
          setPlanningAlgorithmOptions(resp.data ?? []);
        })
        .catch(() => {
          setPlanningAlgorithmOptions([]);
          toast.current?.show({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: 'Не удалось загрузить алгоритмы распределения'
          });
        });
    }, []);

    const taskTypesService = useTaskTypesService();

    const getTaskType = useCallback(async () => {
      const serviceResult = taskTypesService.getWithTaskProcessingProcesses(
        tasksTypeProps.id ? parseInt(tasksTypeProps.id.toString()) : undefined
      );
      serviceResult.result.then((r) => {
        if (r.taskType) {
          reset(toFormTaskTypeValues(r.taskType));
        }
        if (r.taskProcessingProcesses)
          setTaskProcessingProcesses(r.taskProcessingProcesses);
      });
    }, [reset, taskTypesService, tasksTypeProps.id]);

    useNewApiCall(getDefaultOperationsOptions, []);
    useNewApiCall(loadPlanningAlgorithmOptions, []);
    useNewApiCall(getTaskType, [reset, taskTypesService, tasksTypeProps.id]);

    const onSubmit: SubmitHandler<TaskType> = async (data) => {
      const newData = { ...data };

      if (data.priority !== undefined) {
        newData.priority = parseInt(data.priority.toString());
      }

      if (
        data.requiredEmployees !== undefined &&
        data.requiredEmployees !== null
      ) {
        newData.requiredEmployees = parseInt(data.requiredEmployees.toString());
      }

      if (data.processingTime !== null && data.processingTime !== undefined) {
        newData.processingTime = parseInt(data.processingTime.toString());
      }
      if (!data.statusTransition?.id) newData.statusTransition = undefined;
      if (!data.planningAlgorithm?.id) {
        newData.planningAlgorithm = undefined;
      }
      newData.requiredFields = {
        ...newData.requiredFields,
        attachment: false
      };
      if (isEffortManuallyTouched) {
        newData.isCustomEffort = true;
      }
      newData.responseTime = toTimeWithSeconds(data.responseTime);
      newData.resolutionTime = toTimeWithSeconds(data.resolutionTime);

      const currentAutoEffort = calculateAutoProcessingTime(
        newData.defaultOperations || []
      );
      const currentProcessingTime =
        newData.processingTime === null || newData.processingTime === undefined
          ? null
          : Number(newData.processingTime);

      if (
        !isEffortManuallyTouched &&
        newData.isCustomEffort === false &&
        currentProcessingTime !== currentAutoEffort
      ) {
        setPendingSubmitData(newData);
        setRecalculatedEffort(currentAutoEffort);
        setShowAutoEffortDialog(true);
        return;
      }
      await saveTaskTypeData(newData);
    };

    const handleSaveResult = (result: number | undefined) => {
      if (result === -1) {
        setError('taskType', {
          type: 'value',
          message:
            'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
        });
        return;
      }

      if (result === -2) {
        setError('processingTime', {
          type: 'value',
          message:
            'Суммарное время плановых трудозатрат и планового срока начала не должно превышать плановый срок завершения'
        });
        return;
      }

      if (result === -3) {
        setError('taskType', {
          type: 'value',
          message: 'Требуемые работники должны быть в диапазоне от 0 до 1000'
        });
        toast.current?.show({
          severity: 'warn',
          summary: 'Ошибка сохранения',
          detail: 'Требуемые работники должны быть в диапазоне от 0 до 1000',
          life: 5000
        });
        return;
      }

      closeDialog && closeDialog();
    };

    const saveTaskTypeData = async (dataToSave: TaskType) => {
      let result: number | undefined = 0;
      if (onUpdate && dataToSave.id) {
        result = await onUpdate(dataToSave.id, dataToSave);
      }
      if (onAdd && !dataToSave.id) {
        const addData = { ...dataToSave, id: 0 };
        result = await onAdd(addData);
      }
      handleSaveResult(result);
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else if (closeDialog) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDataTableDropdownSelectionChange = (
      selected: ObjectForSelection | ObjectForSelection[] | null
    ) => {
      if (Array.isArray(selected)) {
        setValue('defaultOperations', selected, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else if (selected && !Array.isArray(selected)) {
        setValue('defaultOperations', [selected], {
          shouldDirty: true,
          shouldValidate: true
        });
      } else {
        setValue('defaultOperations', [], {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    };

    const handleAutoEffortCalculation = () => {
      const autoEffort = calculateAutoProcessingTime(defaultOperations || []);
      setValue('processingTime', autoEffort, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue('isCustomEffort', false, {
        shouldDirty: true
      });
      setIsEffortManuallyTouched(false);
    };

    const handleSaveWithRecalculation = async () => {
      if (!pendingSubmitData || recalculatedEffort === null) {
        setShowAutoEffortDialog(false);
        return;
      }
      const dataToSave = {
        ...pendingSubmitData,
        processingTime: recalculatedEffort,
        isCustomEffort: false
      };
      setValue('processingTime', recalculatedEffort, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue('isCustomEffort', false, { shouldDirty: true });
      setShowAutoEffortDialog(false);
      setPendingSubmitData(null);
      setIsEffortManuallyTouched(false);
      await saveTaskTypeData(dataToSave);
    };

    const handleSaveWithoutRecalculation = async () => {
      if (!pendingSubmitData) {
        setShowAutoEffortDialog(false);
        return;
      }
      const dataToSave = {
        ...pendingSubmitData,
        isCustomEffort: true
      };
      setValue('isCustomEffort', true, { shouldDirty: true });
      setShowAutoEffortDialog(false);
      setPendingSubmitData(null);
      setIsEffortManuallyTouched(true);
      await saveTaskTypeData(dataToSave);
    };

    const currentAutoProcessingTime = calculateAutoProcessingTime(
      defaultOperations || []
    );
    const hasAutoEffortMismatch =
      isCustomEffort === false &&
      Number(processingTime ?? 0) !== Number(currentAutoProcessingTime);
    const mainErrors = Boolean(
      errors.taskType ||
        errors.statusTransition ||
        errors.defaultOperations ||
        errors.responseTime ||
        errors.resolutionTime ||
        errors.requiredEmployees ||
        errors.processingTime
    );

    const handleDeleteTag = (taskId: string | number) => {
      const newdefaultOperations = defaultOperations.filter(
        (defaultOperationsOption) =>
          defaultOperationsOption.id?.toString() !== taskId.toString()
      );
      setValue('defaultOperations', newdefaultOperations, {
        shouldDirty: true,
        shouldValidate: true
      });
    };

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(businessProcessId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleRequiredFieldsUpdate = (
      updatedRequiredFields: TaskType['requiredFields']
    ) => {
      setValue(
        'requiredFields',
        { ...updatedRequiredFields, attachment: false },
        {
          shouldDirty: true,
          shouldValidate: true
        }
      );
      setShowRequiredFieldsDialog(false);
    };

    const handleRequiredFieldsDialogHide = () => {
      requiredFieldsControlRef.current?.handleCancel();
    };

    return (
      <div className="taskType-card taskType-card__roles">
        <Toast ref={toast} />
        <Dialog
          header="Контроль заполнения полей"
          visible={showRequiredFieldsDialog}
          style={{
            width: '600px',
            minWidth: '600px',
            minHeight: 'fit-content'
          }}
          contentStyle={{
            maxHeight: '90vh'
          }}
          maximizable
          modal
          onHide={handleRequiredFieldsDialogHide}
        >
          {showRequiredFieldsDialog && (
            <TaskTypeRequiredFieldsControl
              requiredFields={
                requiredFields ?? DEFAULT_TASK_TYPE_REQUIRED_FIELDS
              }
              onUpdate={handleRequiredFieldsUpdate}
              closeDialog={() => setShowRequiredFieldsDialog(false)}
              ref={requiredFieldsControlRef}
            />
          )}
        </Dialog>
        <TabView>
          <TabPanel header="Основная" className={mainErrors ? 'error-tab' : ''}>
            <div className=" gap-3 pb-0">
              <div className="tasktype-card__column">
                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="taskType" className="col p-0">
                        Название типа наряда<span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0">
                        <InputText
                          className="user-card__input w-full"
                          {...register('taskType')}
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.taskType ? 'text-red-500' : 'text-transparent'
                      } `}
                    >
                      {errors.taskType ? errors.taskType.message : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="statusTransition" className="col p-0">
                        Процесс обработки наряда:
                      </label>
                      <div className="col-8 p-0">
                        <CustomDropdown<ObjectForSelection>
                          assetOptions={taskProcessingProcesses}
                          selectedAsset={statusTransition ?? null}
                          onSelectionChange={(selected) => {
                            setValue(
                              'statusTransition',
                              (selected as ObjectForSelection) || undefined,
                              {
                                shouldDirty: true,
                                shouldValidate: true
                              }
                            );
                          }}
                          placeholder="Выберите процесс обработки наряда"
                          emptyMessage="Нет процессов обработки для выбора."
                          showDeletedWarning
                          deletedWarningTooltip="Удалено в справочнике"
                          showClearOnHover
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.statusTransition
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.statusTransition
                        ? (errors.statusTransition as any)?.message
                        : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="planningAlgorithm" className="col p-0">
                        Алгоритм распределения:
                      </label>
                      <div className="col-8 p-0">
                        <CustomDropdown<ObjectForSelection>
                          assetOptions={planningAlgorithmOptions}
                          selectedAsset={planningAlgorithm ?? null}
                          onSelectionChange={(selected) => {
                            setValue(
                              'planningAlgorithm',
                              (selected as ObjectForSelection) || undefined,
                              {
                                shouldDirty: true,
                                shouldValidate: true
                              }
                            );
                          }}
                          placeholder="Выберите алгоритм распределения"
                          emptyMessage="Нет алгоритмов для выбора."
                          showDeletedWarning
                          deletedWarningTooltip="Удалено в справочнике"
                          showClearOnHover
                        />
                      </div>
                    </div>
                    <p className="task-type-card-errors card-errors text-transparent">
                      {' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="defaultOperations" className="col p-0">
                        Приоритет:
                      </label>
                      <div className="col-8 p-0">
                        <CustomDropdown
                          assetOptions={priorityOptions}
                          selectedAsset={
                            priorityOptions.find((o) => o.id === priority) ??
                            null
                          }
                          onSelectionChange={(selected) => {
                            const priorityValue =
                              selected !== null &&
                              (selected as any).id !== undefined
                                ? Number((selected as any).id)
                                : undefined;
                            setValue('priority', priorityValue, {
                              shouldDirty: true,
                              shouldValidate: true
                            });
                          }}
                          placeholder="Выберите приоритет"
                          showClearOnHover
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.defaultOperations
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.defaultOperations
                        ? (errors.defaultOperations as any)?.message
                        : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="responseTime" className="col p-0">
                        Плановый срок начала работ
                        <span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0">
                        <InputText
                          className="user-card__input w-full"
                          placeholder="чч:мм или ччч:мм"
                          value={responseTime ?? ''}
                          onChange={(e) =>
                            setValue('responseTime', e.target.value ?? '', {
                              shouldDirty: true,
                              shouldValidate: true
                            })
                          }
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.responseTime
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.responseTime ? errors.responseTime.message : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="resolutionTime" className="col p-0">
                        Плановый срок завершения работ
                        <span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0">
                        <InputText
                          className="user-card__input w-full"
                          placeholder="чч:мм или ччч:мм"
                          value={resolutionTime ?? ''}
                          onChange={(e) =>
                            setValue('resolutionTime', e.target.value ?? '', {
                              shouldDirty: true,
                              shouldValidate: true
                            })
                          }
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.resolutionTime
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.resolutionTime
                        ? errors.resolutionTime.message
                        : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="requiredEmployees" className="col p-0">
                        Треб. кол-во исполнителей
                        <span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0">
                        <InputNumber
                          className="user-card__input w-full"
                          value={requiredEmployees}
                          min={0}
                          max={1000}
                          maxFractionDigits={0}
                          onChange={(e) =>
                            setValue(
                              'requiredEmployees',
                              e.value === null || e.value === undefined
                                ? 0
                                : e.value,
                              {
                                shouldDirty: true,
                                shouldValidate: true
                              }
                            )
                          }
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.requiredEmployees
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.requiredEmployees
                        ? errors.requiredEmployees.message
                        : ' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="defaultOperations" className="col p-0">
                        Плановые работы:
                      </label>
                      <div className="col-8 p-0">
                        <CustomDropdown<ObjectForSelection>
                          multiplySelectMode
                          assetOptions={defaultOperationsOptions}
                          selectedAsset={defaultOperations}
                          onSelectionChange={
                            handleDataTableDropdownSelectionChange
                          }
                          placeholder="Выбор плановых работ"
                          emptyMessage="Нет плановых работ для выбора."
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.defaultOperations
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.defaultOperations
                        ? (errors.defaultOperations as any)?.message
                        : ' '}
                    </p>
                  </div>
                </div>

                <div className="grid flex justify-content-between align-items-center mb-3 ml-0 mr-0">
                  <label style={{ width: '130px' }} htmlFor="priority"></label>
                  {defaultOperations?.length > 0 && (
                    <div
                      style={{
                        width: '100%',
                        maxHeight: '136px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '8px'
                      }}
                      id="tags-scroll"
                    >
                      <TagsComponent
                        passedClassName="gap-2"
                        initialTagsItems={markDeletedItems<ObjectForSelection>(
                          defaultOperations,
                          defaultOperationsOptions
                        )}
                        onDelete={handleDeleteTag}
                      />
                    </div>
                  )}
                  {(!defaultOperations || defaultOperations.length < 1) && (
                    <div
                      style={{
                        width: '100%',
                        maxHeight: '136px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '8px'
                      }}
                      id="tags-scroll"
                    >
                      Плановыe работы не выбраны
                    </div>
                  )}
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="processingTime" className="col p-0">
                        Плановые трудозатраты (мин)
                        <span className="required">*</span>:
                      </label>
                      <div className="col-8 p-0 flex align-items-center gap-2">
                        <div className="w-full">
                          <InputNumber
                            className="user-card__input w-full"
                            value={value}
                            min={0}
                            max={1000000}
                            maxFractionDigits={0}
                            onChange={(e) => {
                              onChange(e.value);
                              setIsEffortManuallyTouched(true);
                              setValue('isCustomEffort', true, {
                                shouldDirty: true
                              });
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          icon="pi pi-calculator"
                          outlined
                          tooltip="Автоматический расчет исходя из плановых значений трудозатрат по работам"
                          tooltipOptions={{ position: 'top' }}
                          onClick={handleAutoEffortCalculation}
                        />
                      </div>
                    </div>
                    <p
                      className={`task-type-card-errors card-errors ${
                        errors.processingTime
                          ? 'text-red-500'
                          : 'text-transparent'
                      } `}
                    >
                      {errors.processingTime
                        ? errors.processingTime.message
                        : ' '}
                    </p>
                    <p
                      className={`task-type-card-errors card-errors ${
                        isCustomEffort === false
                          ? 'text-blue-500'
                          : 'text-transparent'
                      }`}
                    >
                      {isCustomEffort === false
                        ? 'Автоматический расчет включен'
                        : ' '}
                    </p>
                    {hasAutoEffortMismatch && (
                      <p className="task-type-card-errors card-errors text-orange-500">
                        Состав плановых работ изменен. Для актуализации
                        трудозатрат выполните пересчет.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label htmlFor="inventory" className="col p-0">
                        Инвентаризация:
                      </label>
                      <div className="col-8 p-0 flex justify-content-end">
                        <InputSwitch
                          inputId="inventory"
                          checked={inventory ?? false}
                          onChange={(e) =>
                            setValue('inventory', e.value ?? false, {
                              shouldDirty: true,
                              shouldValidate: true
                            })
                          }
                        />
                      </div>
                    </div>
                    <p className="task-type-card-errors card-errors text-transparent">
                      {' '}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Настройки обязательных полей">
            <div className=" gap-3 pb-0">
              <div className="tasktype-card__column">
                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex-row align-items-center gap-3 w-full">
                      <label
                        htmlFor="assignDepartmentOnSave"
                        className="col-11 p-0"
                      >
                        Запускать автоназначение нарядов на участки при создании
                        наряда:
                      </label>
                      <div className="col-1 p-0 flex justify-content-end">
                        <InputSwitch
                          inputId="assignDepartmentOnSave"
                          checked={assignDepartmentOnSave ?? false}
                          onChange={(e) =>
                            setValue(
                              'assignDepartmentOnSave',
                              e.value ?? false,
                              {
                                shouldDirty: true,
                                shouldValidate: true
                              }
                            )
                          }
                        />
                      </div>
                    </div>
                    <p className="task-type-card-errors card-errors text-transparent">
                      {' '}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex flex-row align-items-center gap-3 w-full">
                      <label htmlFor="requiredFields" className="col-11 p-0">
                        Настроить обязательные поля при создании наряда:
                      </label>
                      <div className="col-1 p-0 flex justify-content-end">
                        <Button
                          type="button"
                          rounded
                          severity="secondary"
                          icon="pi pi-cog"
                          tooltip="Контроль заполнения полей"
                          onClick={() => setShowRequiredFieldsDialog(true)}
                        />
                      </div>
                    </div>
                    <p className="task-type-card-errors card-errors text-transparent">
                      {' '}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="тип наряда"
          entityName={taskType}
        />
        <ConfirmationDialog
          visible={showAutoEffortDialog}
          onHide={() => setShowAutoEffortDialog(false)}
          onCancel={handleSaveWithoutRecalculation}
          onContinue={handleSaveWithRecalculation}
          dialogStyle={{ width: '36vw', maxWidth: '630px' }}
          header="Требуется пересчет трудозатрат"
          confirmLabel="Пересчитать и сохранить"
          cancelLabel="Сохранить текущее значение"
          message={`Плановые трудозатраты не совпадают с актуальной суммой по выбранным работам.${
            recalculatedEffort !== null
              ? ` Актуальное рассчитанное значение: ${recalculatedEffort} мин.`
              : ''
          } Можно пересчитать значение и сохранить карточку либо сохранить текущее значение, но тогда признак автоматического расчета будет снят.`}
        />
      </div>
    );
  }
);

TaskTypeCard.displayName = 'TaskTypeCard';

export default TaskTypeCard;
